import React, { useMemo, useRef, useEffect, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei'
import { FilterClock, useCameraControls, Z, Y, selectSetActiveOrientationObject, selectSetUpAndForward, selectAddParent, selectRemoveParent, selectRadius } from './CameraControls';
import { checkLimits } from '../../machines';
import { DMU95 } from '../../constants/machine-state/machine';
import { PIVOTX, PIVOTY, PIVOTZ, DY, DZ, toolLength } from '../../machines/dmu95';

export default function Model({ 
  joints = [ 0,0,0,0,0],
  tool = null,
  toolHolder = "NONE",
  spindleAngle = 0,
  model = null,
  targetFPS = 120,
  modelOffsets = { X: 0, Y: 0, Z:0, A: 0, B: 0, C: 0 },
  smoothJoints = 1,
  children,
  ...props 
}) {
  const group = useRef()
  const { scene } = useThree();
  const { nodes, materials } = useGLTF('./gltf/dmu95monoBlock.glb')

  const baseRef = useRef();
  const spindleRef = useRef();
  const spindleHousingRef = useRef();
  const tableRef = useRef();
  const trunnionRef = useRef();
  const partRef = useRef();

  const addParent = useCameraControls(selectAddParent);
  const setActiveOrientationObject = useCameraControls(selectSetActiveOrientationObject);
  const removeParent = useCameraControls(selectRemoveParent);
  const setUpAndForward = useCameraControls(selectSetUpAndForward);
  const radius = useCameraControls(selectRadius);

  const jointColors = useMemo(() => joints.map((joint, jointIndex) => checkLimits(DMU95, jointIndex, joint) ? 0x000000 : 0xaa0000 ), [ joints ]);
  const holder = nodes[toolHolder] ? <primitive object={nodes[toolHolder]} rotation-z={spindleAngle}/> : null;

  const [ clock ]  = useState(new FilterClock());

  useFrame(() => {
    const performFiltering = () => {
      const f = .95;
      const minRadius = 50;
      const maxRadius = 150;
      const opacity = radius > maxRadius ? 1 : Math.max(radius-minRadius, 0)/(maxRadius-minRadius);
      materials.BaseMaterialDark.transparent = true;
      materials.BaseMaterialDark.opacity = materials.BaseMaterialDark.opacity*f + (1-f)*opacity;
      materials.BaseMaterialLight.transparent = true;
      materials.BaseMaterialLight.opacity = materials.BaseMaterialLight.opacity*f + (1-f)*opacity;
      materials.BaseMaterialMetallic.transparent = true;
      materials.BaseMaterialMetallic.opacity = materials.BaseMaterialMetallic.opacity*f + (1-f)*opacity;

      if(spindleHousingRef.current) {
        spindleHousingRef.current.position.x = spindleHousingRef.current.position.x*(1-smoothJoints) + smoothJoints*joints[0]; 
        spindleHousingRef.current.position.y = spindleHousingRef.current.position.y*(1-smoothJoints) + smoothJoints*joints[1]; 
      }

      if(spindleRef.current) {
        spindleRef.current.position.z = spindleRef.current.position.z*(1-smoothJoints) + smoothJoints*toolLength(joints[2]);
      }

      if(trunnionRef.current) {
        trunnionRef.current.rotation.x = trunnionRef.current.rotation.x*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[3];
      }

      if(tableRef.current) {
        tableRef.current.rotation.z= tableRef.current.rotation.z*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[4];
      }
    }
    let deltaT = clock.getElapsedTime();
    const singleFrameTime = 1000/targetFPS;
    while(deltaT > singleFrameTime) {
      performFiltering();
      deltaT -= singleFrameTime;
      clock.offsetStart(singleFrameTime);
    }
  });

  useEffect(() => {
    const intensity = 1.4;
    const intensity2 = 1.5;
    materials.BaseMaterialDark.envMap = scene.environment;
    materials.BaseMaterialDark.envMapIntensity = intensity;
    materials.BaseMaterialLight.envMap = scene.environment;
    materials.BaseMaterialLight.envMapIntensity = intensity;
    materials.BaseMaterialMetallic.envMap = scene.environment;
    materials.BaseMaterialMetallic.envMapIntensity = intensity;

    materials.TrunnionMaterial.envMap = scene.environment;
    materials.TrunnionMaterial.envMapIntensity = intensity;

    materials.TableMaterial.envMap = scene.environment;
    materials.TableMaterial.envMapIntensity = intensity2;

    materials.SpindleHousingMaterialDark.envMap = scene.environment;
    materials.SpindleHousingMaterialDark.envMapIntensity = intensity;

    materials.SpindleHousingMaterialLight.envMap = scene.environment;
    materials.SpindleHousingMaterialLight.envMapIntensity = intensity;

    materials.SpindleMaterial.envMap = scene.environment;
    materials.SpindleMaterial.envMapIntensity = intensity2;

    materials.SpindleBaseMaterial.envMap = scene.environment;
    materials.SpindleBaseMaterial.envMapIntensity = intensity;

    materials.H1Material.envMap = scene.environment;
    materials.H1Material.envMapIntensity = intensity2;
    materials.H2Material.envMap = scene.environment;
    materials.H2Material.envMapIntensity = intensity2;
    materials.H3Material.envMap = scene.environment;
    materials.H3Material.envMapIntensity = intensity2;
    materials.H4Material.envMap = scene.environment;
    materials.H4Material.envMapIntensity = intensity2;
    materials.H5Material.envMap = scene.environment;
    materials.H5Material.envMapIntensity = intensity2;
    materials.H6Material.envMap = scene.environment;
    materials.H6Material.envMapIntensity = intensity2;
    materials.H7Material.envMap = scene.environment;
    materials.H7Material.envMapIntensity = intensity2;
    materials.H8Material.envMap = scene.environment;
    materials.H8Material.envMapIntensity = intensity2;
    materials.H9Material.envMap = scene.environment;
    materials.H9Material.envMapIntensity = intensity2;
    materials.H10Material.envMap = scene.environment;
    materials.H10Material.envMapIntensity = intensity2;
    materials.H11Material.envMap = scene.environment;
    materials.H11Material.envMapIntensity = intensity2;
    materials.H12Material.envMap = scene.environment;
    materials.H12Material.envMapIntensity = intensity2;
    materials.H13Material.envMap = scene.environment;
    materials.H13Material.envMapIntensity = intensity2;
    materials.H14Material.envMap = scene.environment;
    materials.H14Material.envMapIntensity = intensity2;
    materials.H15Material.envMap = scene.environment;
    materials.H15Material.envMapIntensity = intensity2;
    materials.H16Material.envMap = scene.environment;
    materials.H16Material.envMapIntensity = intensity2;
    materials.H17Material.envMap = scene.environment;
    materials.H17Material.envMapIntensity = intensity2;

    const base = { name: "Base", object: baseRef.current };
    const spindle = { name: "Spindle", object: spindleRef.current };
    const table = { name: "Table", object: tableRef.current };
    const part = { name: "Part", object: partRef.current };
    const trunnion = { name: "Trunnion", object: trunnionRef.current };

    addParent(base);
    setActiveOrientationObject(base);

    addParent(spindle);
    addParent(table);
    addParent(trunnion);
    addParent(part);

    setUpAndForward(Z, Y);

    return () => {
      removeParent(base);
      removeParent(spindle);
      removeParent(table);
      removeParent(trunnion);
      removeParent(part);
    };
  }, [ addParent, materials, removeParent, scene, group, spindleRef, tableRef, trunnionRef, baseRef, setUpAndForward, setActiveOrientationObject ]);

  const dist = 4;
  return (
    <group ref={group} {...props} dispose={null}>
      <mesh geometry={nodes.base_1.geometry} material={materials.BaseMaterialLight} rotation-x={-Math.PI/2}/>
      <mesh geometry={nodes.base_3.geometry} material={materials.BaseMaterialMetallic} rotation-x={-Math.PI/2}/>
      <mesh geometry={nodes.base_2.geometry} material={materials.BaseMaterialDark} rotation-x={-Math.PI/2}>
        <group ref={baseRef}/>
        <spotLight
          intensity={0.35}
          angle={Math.PI / 8}
          penumbra={0.15}
          decay={2}
          position={[-50*dist, -50*dist, 50*dist]}
          rotation={[Math.PI / 4, -0.62, -Math.PI / 6]}
        />
        <spotLight
          intensity={0.35}
          angle={Math.PI / 8}
          penumbra={0.15}
          decay={2}
          position={[56.27*dist, -35.4*dist, 4.86*dist]}
          rotation={[Math.PI / 4, 0.62, Math.PI / 6]}
        />
        <spotLight
          intensity={0.07}
          angle={Math.PI / 8}
          penumbra={0.15}
          decay={2}
          position={[61.42*dist, 41.42*dist, 17.29*dist]}
          rotation={[-Math.PI / 4, 0.62, 2.62]}
        />
        <spotLight
          intensity={0.07}
          angle={Math.PI / 8}
          penumbra={0.15}
          decay={2}
          position={[-24.92*dist, 44.85*dist, 50*dist]}
          rotation={[-Math.PI / 4, -0.62, -2.62]}
        />
        <spotLight
          intensity={0.07}
          angle={Math.PI / 8}
          penumbra={0.15}
          decay={2}
          position={[-29.16*dist, 69.99*dist, -49.41*dist]}
          rotation={[-2.36, -0.62, 2.62]}
        />
        <mesh geometry={nodes.trunnion.geometry} material={materials.TrunnionMaterial} position={[20.0788, -13.97536, -27.53613]} ref={trunnionRef} material-emissive={jointColors[3]}>
          <mesh geometry={nodes.table.geometry} material={materials.TableMaterial} position={[0, -2.75394, -2.75591]} ref={tableRef} material-emissive={jointColors[4]}>
            <group position-x={-PIVOTX} position-y={-PIVOTY-DY} position-z={-PIVOTZ-DZ}>
              <group position={[ modelOffsets.X, modelOffsets.Y, modelOffsets.Z ]} rotation-x={ modelOffsets.A*Math.PI/180 } rotation-z={ modelOffsets.C*Math.PI/180 }>
                <group ref={partRef}></group>
                {model}
              </group>
              {children}
            </group>
          </mesh>
        </mesh>
        <group ref={spindleHousingRef}>
          <mesh geometry={nodes.spindleHousing_1.geometry} material={materials.SpindleHousingMaterialLight} material-emissive={jointColors[0] | jointColors[1]}/>
          <mesh geometry={nodes.spindleHousing_2.geometry} material={materials.SpindleHousingMaterialDark} material-emissive={jointColors[0] | jointColors[1]}/>
          <group ref={spindleRef}>
            <mesh geometry={nodes.spindle_1.geometry} material={materials.SpindleMaterial} material-emissive={jointColors[2]} />
            <mesh geometry={nodes.spindle_2.geometry} material={materials.SpindleBaseMaterial} material-emissive={jointColors[2]}/>
            { tool }
            { holder }
          </group>
        </group>
      </mesh>
    </group>
  )
}
